import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { type FAQItemType } from "@vega-core/models/faq.type";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { trackRegistrationStarted } from "@vega-core/utils/posthog/onboardingAnalytics";

import { InvalidCodeMessage } from "./components/InvalidCodeMessage";
import { RegistrationLayout } from "./components/RegistrationLayout";
import { InvitationRegistrationForm } from "./InvitationRegistrationForm";
import { type InvitationCheckHook, type InvitationCompleteHook } from "./types";

type Props = {
  useInvitationCheckHook: InvitationCheckHook;
  useInvitationCompleteHook: InvitationCompleteHook;
  loginPath: string;
  faqItems?: FAQItemType[];
  headerTitle?: string;
};

export const InvitationRegistration = ({
  useInvitationCheckHook,
  useInvitationCompleteHook,
  loginPath,
  faqItems,
  headerTitle,
}: Props) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitationCode = searchParams.get("invitationCode");

  const { data, isError, isLoading, isFetched } = useInvitationCheckHook(
    { invitationCode: invitationCode! },
    {
      query: {
        retry: 2,
        enabled: isTruthy(invitationCode),
        refetchOnWindowFocus: false,
      },
    },
  );

  const isRegistrationComplete =
    isError && Boolean(sessionStorage.getItem("userRegistrationComplete"));

  useEffect(() => {
    if (isRegistrationComplete) {
      sessionStorage.removeItem("userRegistrationComplete");
      navigate(loginPath, { replace: true });
    }
  }, [isRegistrationComplete, navigate, loginPath]);

  useEffect(() => {
    if (isFetched && !isError && data) {
      trackRegistrationStarted({ invitedUserId: data.invitedUserId });
    }
  }, [isFetched, isError, data]);

  if (isFalsy(invitationCode)) {
    return (
      <RegistrationLayout faqItems={faqItems} title={headerTitle}>
        <InvalidCodeMessage />
      </RegistrationLayout>
    );
  }

  if (isLoading || !isFetched) {
    return <LoadingSpinner />;
  }

  return (
    <RegistrationLayout faqItems={faqItems} title={headerTitle}>
      {isError ? (
        <InvalidCodeMessage />
      ) : (
        <InvitationRegistrationForm
          invitationCheckData={data}
          invitationCode={invitationCode}
          useInvitationCompleteHook={useInvitationCompleteHook}
        />
      )}
    </RegistrationLayout>
  );
};
